import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  InputAdornment,
} from "@mui/material";
import { ScanLine, UserCheck } from "lucide-react";
import { API_ENDPOINT, ONSITE_MODULE_ITEMS } from "../../utils/constants";
import { toastError } from "../../utils/util";
import { getOnsiteHeader } from "./utils";
import { EbadgeService } from "./EbadgeService";

export const getCategoryName = (userObj) => {
  const category = userObj.user_type
    ? userObj.user_type
    : "Pre-Registered Visitor";
  return category;
};

function ServiceBadgeScan() {
  const [currentUser, setCurrentUser] = useState();
  const inputRef = useRef();

  const handleScanInput = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const scannedValue = event.target.value;
      if (!scannedValue.trim()) return;

      inputRef.current.value = "";
      loadUser(scannedValue);
    }
  };

  useEffect(() => {
    // Keep focus on input for continuous scanning
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentUser]); // Re-focus after scanning a new user

  async function loadUser(id) {
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/onsite/get-service-badge-barcode?id=${id}`,
        getOnsiteHeader(ONSITE_MODULE_ITEMS.SERVICE_BADGE_SCAN)
      );

      if (response.status === 200) {
        toast.success("Successfully Scanned!", { autoClose: 1000 });
        setCurrentUser(response.data);
      } else if (response.status === 204) {
        toast.info("Invalid QR or Badge Not Found!", { autoClose: 2000 });
        setCurrentUser(null);
      }
    } catch (error) {
      toastError(error);
      setCurrentUser(null);
    }
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* Compact Scanner Input Section */}
      <Paper
        elevation={2}
        sx={{
          py: 2,
          px: 3,
          mb: 3,
          borderRadius: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
          gap: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} color="text.primary">
          Service Badge Scanner
        </Typography>

        <TextField
          inputRef={inputRef}
          id="barcodeInput"
          onKeyDown={handleScanInput}
          placeholder="Scan Badge QR / Barcode..."
          variant="outlined"
          size="small"
          autoFocus
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ScanLine size={20} color="#1976d2" />
              </InputAdornment>
            ),
            sx: {
              fontSize: "1rem",
              borderRadius: 2,
              bgcolor: "grey.50",
            },
          }}
          sx={{ width: "100%", maxWidth: 350 }}
        />
      </Paper>

      {/* Result Display Section */}
      {currentUser ? (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="center"
          >
            {/* Left Side: Profile Picture */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 320,
                  p: 1.5,
                  borderRadius: 3,
                  border: "2px dashed",
                  borderColor: "grey.300",
                  bgcolor: "grey.50",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
              >
                {currentUser.photoUrl ? (
                  <Box
                    component="img"
                    src={currentUser.photoUrl}
                    alt="Profile"
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxHeight: 400,
                      objectFit: "cover",
                      borderRadius: 2,
                      display: "block",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "grey.400",
                    }}
                  >
                    <UserCheck size={64} />
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Right Side: Ebadge Component */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <EbadgeService isPreview={true} user={currentUser} />
            </Grid>
          </Grid>
        </Paper>
      ) : (
        /* Empty State */
        <Paper
          elevation={1}
          sx={{
            minHeight: "50vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 3,
            bgcolor: "grey.50",
            border: "1px dashed",
            borderColor: "grey.300",
          }}
        >
          <Box sx={{ textAlign: "center", color: "text.secondary" }}>
            <ScanLine
              size={64}
              style={{ opacity: 0.3, marginBottom: "16px" }}
            />
            <Typography variant="h6" fontWeight={500} color="text.primary">
              Ready to Scan
            </Typography>
            <Typography variant="body2" mt={1}>
              Please scan a badge barcode to view visitor details.
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default ServiceBadgeScan;
